import type { HttpClient } from '@/lib/http/client.js'
import type { Credentials, LoginResponse, Operator } from '@/types/auth/auth.type.js'

/**
 * The credential endpoints of `zapio-backend`'s admin API.
 *
 * Paths are verified against `admin-auth.controller.ts` and `admin-account.controller.ts`,
 * not against the Swagger page. **This is the only file in the SPA where these strings
 * live** (architecture §3.1).
 *
 * A factory, with no instance of its own: `bootstrap.ts` constructs the client, builds this
 * once and provides it. That is what keeps the client out of every other module — and it is
 * what lets a test pass a fake with no environment, no `fetch` and no app.
 *
 * ## No method here returns a token, and none can
 *
 * The session arrives as two `httpOnly` cookies on the `Set-Cookie` header, which
 * JavaScript cannot read — so there is nothing for these methods to return but the
 * operator. `login` and `me` answer with the operator **directly**, not wrapped in
 * `{ actor: … }`; that is what the mapper produces, and a guessed wrapper typechecks and
 * then renders a blank screen.
 */
export interface AuthService {
  /**
   * Sets the session cookies — or, for an operator with an authenticator, sets a five-minute
   * challenge cookie and answers `{ two_factor_required: true }`. Rejects 401 on bad
   * credentials, 429 when locked out.
   */
  login: (credentials: Credentials) => Promise<LoginResponse>
  /**
   * The second step. The challenge is the `httpOnly` cookie `login` set; this sends only the
   * code. 401 for a wrong code, an expired challenge, or none at all. Shares the lockout.
   */
  completeTwoFactor: (code: string) => Promise<Operator>
  /** The operator and their CURRENT permissions. 401 simply means "not signed in". */
  me: () => Promise<Operator>
  /**
   * Exchange the refresh cookie for a new session.
   *
   * Takes no body: the refresh token is the `httpOnly` cookie the backend scopes to this
   * one endpoint, so it is absent from every other request and cannot leak through their
   * logs. Succeeding ROTATES the pair, which is why only one of these may be in flight.
   */
  refresh: () => Promise<Operator>
  /** Expires both cookies. Succeeds even for a session that has already expired. */
  logout: () => Promise<{ signedOut: true }>
  /**
   * Changes the caller's own password.
   *
   * The current password is required **even though the caller is signed in**: a cookie is a
   * bearer credential, so without that check anyone holding a stolen session could lock
   * the real operator out of their own account. Succeeding revokes every session including
   * this one, so the panel must sign in again — that is the point, not a defect.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ changed: true }>
  /**
   * Asks for a reset link. **Always resolves**, whether or not the address has an account —
   * the backend answers 202 either way, so this cannot be used to enumerate operators.
   * Throttled upstream to 3 per address and 10 per IP every 15 minutes; a 429 rejects.
   */
  requestPasswordReset: (email: string) => Promise<void>
  /**
   * Sets a new password from an emailed link. The token works once and expires after an
   * hour; invalid, expired and already-used all answer the same 400. Succeeding revokes
   * every session for the account, so the only next step is the login screen.
   */
  completePasswordReset: (token: string, password: string) => Promise<{ reset: true }>
}

export function createAuthService(http: HttpClient): AuthService {
  return {
    login: (credentials) => http.post<LoginResponse>('admin/auth/login', credentials),
    completeTwoFactor: (code) => http.post<Operator>('admin/auth/two-factor', { code }),
    me: () => http.get<Operator>('admin/auth/me'),
    // `skipRefresh`: a 401 here means the refresh token itself is dead, and retrying the
    // refresh with the same dead token is an endless loop, not a recovery.
    refresh: () => http.post<Operator>('admin/auth/refresh', undefined, { skipRefresh: true }),
    logout: () => http.post<{ signedOut: true }>('admin/auth/logout'),
    changePassword: (currentPassword, newPassword) =>
      // Snake_case because that is the DTO. The backend rejects unknown body fields
      // globally, so `currentPassword` here would be a 422, not a silent ignore.
      http.post<{ changed: true }>('admin/account/password', {
        current_password: currentPassword,
        new_password: newPassword,
      }),
    requestPasswordReset: async (email) => {
      // The 202 body is discarded on purpose: it says nothing a caller may act on.
      await http.post('admin/account/password-reset/request', { email })
    },
    // Only `token` and `password` — never the client-side confirmation. The backend
    // rejects unknown body fields globally, so sending it would be a 422.
    completePasswordReset: (token, password) =>
      http.post<{ reset: true }>('admin/account/password-reset/complete', { token, password }),
  }
}
